import { useState } from "react";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { DvsConsentWording } from "./consentCopy";
import { NextButton } from "src/components/application/styled-components/Buttons";
import {
  AdhocKycAuth,
  AdhocKycContextResponse,
  adhocKycErrorMessage,
  useAdhocKycConsentMutation,
} from "src/store/features/api/adhocKyc";
import { AdhocKycBody, AdhocKycError, AdhocKycHeading } from "./AdhocKycShell";

export interface ConsentStepProps {
  auth: AdhocKycAuth;
  context: AdhocKycContextResponse;
}

/**
 * Step 1. The wording is the sentence compliance supplied (CR1, see
 * `consentCopy.tsx`); the version the customer accepted rides in the POST so
 * the backend can pin the immutable consent record to it. The version is an
 * audit detail, not customer copy, so it is never shown on the page.
 */
export default function ConsentStep({ auth, context }: ConsentStepProps): JSX.Element {
  const [accepted, setAccepted] = useState(false);
  const [submit, { isLoading, error }] = useAdhocKycConsentMutation();
  const wordingVersion = String(context.consentWordingVersion);

  const handleContinue = (): void => {
    submit({ auth, body: { wordingVersion, accepted: true } });
  };

  return (
    <Box data-testid="adhoc-kyc-consent-step">
      <AdhocKycHeading>
        {context.firstName ? `Hi ${context.firstName}` : "Hi there"}
      </AdhocKycHeading>
      <AdhocKycBody>
        As part of our obligations under Australian and New Zealand anti-money laundering laws we
        periodically re-verify the identity of our customers. This takes about two minutes. To
        begin, please read and accept the statement below.
      </AdhocKycBody>
      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          p: 2,
          mb: 2,
          bgcolor: "#FFFFFF",
        }}
      >
        <FormControlLabel
          sx={{ alignItems: "flex-start", m: 0 }}
          control={
            <Checkbox
              inputProps={{ "aria-label": "I consent to the identity check" }}
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
              sx={{ pt: 0 }}
            />
          }
          label={<DvsConsentWording />}
        />
      </Box>
      {error ? <AdhocKycError message={adhocKycErrorMessage(error)} /> : null}
      <NextButton
        fullWidth
        disabled={!accepted || isLoading}
        onClick={handleContinue}
        data-testid="adhoc-kyc-consent-continue"
      >
        Continue
      </NextButton>
    </Box>
  );
}
